/**
 * SETTINGS STORE
 *
 * Shape (persisted under the key "settings"):
 *   reduceMotion  boolean   zero every transition and skip event animations
 *   showLog       boolean   is the battlefield log dock open
 *   cardSize      'small' | 'normal' | 'large'   scales mini and hand frames
 *   sound         boolean   placeholder, no audio is wired up in Phase One
 *   botSpeedMs    number    delay between bot actions, 0 with reduceMotion
 *   devMode       boolean   unlocks dev actions and the Test Lab affordances
 *
 * `reduceMotion` and `cardSize` are mirrored onto <html> as data attributes so
 * tokens.css can respond without any component subscribing.
 *
 * Other engineers: add a field, give it a default in DEFAULT_SETTINGS, and it
 * migrates itself because `merge` fills missing keys from the defaults.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { adapterStorage } from '@/persist';

export type CardSize = 'small' | 'normal' | 'large';

export interface SettingsState {
  reduceMotion: boolean;
  showLog: boolean;
  cardSize: CardSize;
  sound: boolean;
  botSpeedMs: number;
  devMode: boolean;
}

export interface SettingsActions {
  set<K extends keyof SettingsState>(key: K, value: SettingsState[K]): void;
  toggle(key: 'reduceMotion' | 'showLog' | 'sound' | 'devMode'): void;
  reset(): void;
}

export const DEFAULT_SETTINGS: SettingsState = {
  reduceMotion: false,
  showLog: true,
  cardSize: 'normal',
  sound: false,
  botSpeedMs: 550,
  devMode: false,
};

export type SettingsStore = SettingsState & SettingsActions;

export const useSettings = create<SettingsStore>()(
  persist(
    (set, get) => ({
      ...DEFAULT_SETTINGS,
      set(key, value) {
        set({ [key]: value } as Partial<SettingsState>);
        applyDocumentSettings(get());
      },
      toggle(key) {
        set({ [key]: !get()[key] } as Partial<SettingsState>);
        applyDocumentSettings(get());
      },
      reset() {
        set({ ...DEFAULT_SETTINGS });
        applyDocumentSettings(get());
      },
    }),
    {
      name: 'settings',
      storage: adapterStorage<SettingsStore>(),
      merge: (persisted, current) => ({
        ...current,
        ...(persisted as Partial<SettingsState> | undefined),
      }),
      onRehydrateStorage: () => (state) => {
        if (state) applyDocumentSettings(state);
      },
    },
  ),
);

/** Mirror the settings that CSS needs onto the document element. */
export function applyDocumentSettings(state: SettingsState): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.setAttribute('data-reduce-motion', state.reduceMotion ? 'true' : 'false');
  root.setAttribute('data-card-size', state.cardSize);
}

/** Effective delay between bot actions, in milliseconds. */
export function botDelay(state: SettingsState): number {
  return state.reduceMotion ? 0 : Math.max(0, state.botSpeedMs);
}
